import React from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import currencyFormatter from 'helpers/currency_formatter';
import dateFormatter from 'helpers/date_formatter';
import Link from '../components/link';

interface PaymentProviderBadgeProps {
  account: any;
  currency: string;
  gocardlessDetail: any;
  provider: any;
}

const bankAccountMap = {
  bluefin: 'ACH',
  ezidebit: 'Direct Debit',
  stripe: 'Bank Account',
  integrapay: 'Direct Debit',
  gocardless: 'Direct Debit',
};

const PaymentProviderBadge = ({
  account,
  currency,
  gocardlessDetail,
  provider,
}: PaymentProviderBadgeProps) => {
  const MasqueradingService = useAngularService('MasqueradingService');
  const EmploymentService = useAngularService('EmploymentService');
  const AdminUserService = useAngularService('AdminUserService');
  const isMasquerading = MasqueradingService.isMasquerading();
  const permissions = EmploymentService.get().permissions;
  const adminHasPermission = AdminUserService.has;

  const displayApplicationFee = (fee) => fee && parseFloat(fee) > 0;

  const bankAccountName = bankAccountMap[provider];

  const getAvailablePaymentMethods = () => {
    switch (true) {
      case account.can_process_bank_accounts && account.can_process_credit_cards:
        return `${bankAccountName} and Credit Cards`;
      case account.can_process_bank_accounts:
        return bankAccountName;
      case account.can_process_credit_cards:
        return 'Credit Cards';
    }
  };

  return (
    <div className="col-sm-7">
      <div className="worx-filled-box payment-connected">
        {['gocardless', 'integrapay'].includes(provider) ? (
          <img className="single-logo connected" src={`/${provider}.svg`} />
        ) : (
          <img className="single-logo connected" src={`/${provider}.png`} />
        )}

        <time>Active since: {dateFormatter(account.created_at)}</time>

        {provider === 'ezidebit' ? <div>Account:&nbsp;{account.account_id}</div> : null}

        {provider === 'stripe' && displayApplicationFee(account.stripe_application_fee) ? (
          <b>fee: {currencyFormatter(account.stripe_application_fee, currency)} per txn</b>
        ) : null}

        {provider === 'gocardless' && displayApplicationFee(account.stripe_application_fee) ? (
          <b>fee: {currencyFormatter(account.application_fee, currency)} per txn</b>
        ) : null}

        <div className="connection-reason">
          <i className="icon-checkmark text-success"></i> Connected for{' '}
          {getAvailablePaymentMethods()}
        </div>

        {provider === 'ezidebit' &&
        isMasquerading &&
        adminHasPermission('admin_manage_contact_payment_methods') ? (
          <div>
            <br />
            <Link type="button" variant="success" route="gym.ezidebit_payers">
              Ezidebit Payers
            </Link>
          </div>
        ) : null}

        {provider === 'stripe' &&
        isMasquerading &&
        adminHasPermission('admin_manage_contact_payment_methods') ? (
          <div>
            <br />
            <Link type="button" variant="success" route="gym.stripe_payers">
              Stripe Payers
            </Link>
          </div>
        ) : null}

        {provider === 'gocardless' ? (
          <div>
            <br />
            {gocardlessDetail.verification_status === 'authorized' ? (
              <>
                <a
                  className="btn btn-primary"
                  href={gocardlessDetail.verify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to Account Verification
                </a>
                <p>
                  <b>Note:</b>
                  Action is required before your GoCardless account can be verified. Please click
                  the button above.
                </p>
              </>
            ) : null}

            <div>Verification status: {gocardlessDetail.humanized_verification_status}</div>

            {permissions.perform_bulk_mandate_imports ? (
              <Link type="button" variant="success" route="gym.gocardless.import_mandates">
                Import Mandates
              </Link>
            ) : null}

            {isMasquerading ? (
              <div>
                <br />
                <Link
                  type="button"
                  variant="primary"
                  target="_blank"
                  route="gym.gocardless.webhook_logs"
                >
                  View Webhook Log
                </Link>
                <Link
                  type="button"
                  variant="primary"
                  target="_blank"
                  route="gym.gocardless.api_logs"
                >
                  View API Log
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentProviderBadge;
